import React from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs } from "antd";
import GlossaryContent from "../components/LearnHub/GlossaryContent";
import TutorialsContent from "../components/LearnHub/TutorialsContent";

const VALID_TABS = ["glossary", "tutorials"];
const DEFAULT_TAB = "glossary";

const VALID_SECTIONS = ["dashboards", "workflows"];
const DEFAULT_SECTION = "dashboards";

// The one page for the glossary and the video tutorials, one tab each.
// App.jsx redirects /glossary and /video_tutorial here, so old bookmarks
// open the matching tab.
const LearnHubPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : DEFAULT_TAB;

  const rawSection = searchParams.get("section");
  const activeSection = VALID_SECTIONS.includes(rawSection)
    ? rawSection
    : DEFAULT_SECTION;

  const handleTabChange = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    if (key !== "tutorials") {
      next.delete("section");
    }
    setSearchParams(next, { replace: true });
  };

  const handleSectionChange = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "tutorials");
    next.set("section", key);
    setSearchParams(next, { replace: true });
  };

  const items = [
    {
      key: "glossary",
      label: "Glossary",
      children: <GlossaryContent />,
    },
    {
      key: "tutorials",
      label: "Video Tutorials",
      children: (
        <TutorialsContent
          section={activeSection}
          onSectionChange={handleSectionChange}
        />
      ),
    },
  ];

  return (
    <div className="flex min-h-[100vh] bg-[#F7F8FA] overflow-auto w-[var(--page-width,calc(100vw-53px))] ml-[var(--page-offset-x,53px)]">
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(8px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .animate-fadeIn {
            animation: fadeIn 0.3s ease-out forwards;
          }
          .learn-hub-tabs .ant-tabs-tab {
            font-family: Inter, sans-serif;
            font-size: 15px;
            padding: 8px 4px;
          }
          .learn-hub-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
            color: #0E49B5 !important;
          }
          .learn-hub-tabs .ant-tabs-ink-bar {
            background: #0E49B5;
          }
          .learn-hub-segmented.ant-segmented {
            background: #F0F2F5;
            padding: 4px;
          }
          .learn-hub-segmented .ant-segmented-item-label {
            color: rgba(0, 0, 0, 0.65);
            font-family: Inter, sans-serif;
            font-weight: 500;
            padding: 0 16px;
          }
          .learn-hub-segmented .ant-segmented-item:hover:not(.ant-segmented-item-selected) .ant-segmented-item-label {
            color: rgba(0, 0, 0, 0.85);
          }
          .learn-hub-segmented .ant-segmented-item-selected {
            background: #FFFFFF;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
          }
          .learn-hub-segmented .ant-segmented-item-selected .ant-segmented-item-label {
            color: #0E49B5;
          }
        `}
      </style>

      <div className="flex-1 flex flex-col gap-6 px-[var(--page-inset-x,1.5rem)] pt-[var(--page-inset-top,1.5rem)] pb-8">
        <h1
          className="text-[30px] font-bold leading-[42px] tracking-[-0.3px] text-[rgba(0,0,0,0.85)]"
          style={{ fontFamily: "Satoshi, sans-serif" }}
        >
          Glossary and Videos
        </h1>

        <Tabs
          className="learn-hub-tabs"
          activeKey={activeTab}
          onChange={handleTabChange}
          items={items}
          size="large"
        />
      </div>
    </div>
  );
};

export default LearnHubPage;
